import { makeModuleI18nSelector, makeModuleStateSelector } from "@client/content/selectors";
import { getSettingValueSelector, TargetSelector } from "@client/selectors";
import { createSelector } from "reselect";
import { defaultState, previewState } from "./reducer";
import { type LoginForm } from "./types";

export const localStateSelector = makeModuleStateSelector<LoginForm.State>(defaultState, previewState);

export const makeLocalStateIsAdminSelector = () => {
  return createSelector([localStateSelector], (ls) => ls.isAdmin);
};

export const makeLocalStateLoginUrlSelector = () => {
  return createSelector([localStateSelector], (ls) => ls.loginUrl);
};

export const makeLocalStateStatusSelector = () => {
  return createSelector([localStateSelector], (ls) => ls.status);
};

export const makeLocalStateCanLoginSelector = () => {
  return createSelector([localStateSelector], (ls) => ls.canLogin);
};

export const makeLocalStateFieldErrorsSelector = () => {
  return createSelector([localStateSelector], (ls) => ls.fieldErrors);
};

export const makeLocalStateIsLinkSentSelector = () => {
  return createSelector([localStateSelector], (ls) => ls.isLinkSent);
};

export const makeLocalStateModeSelector = () => {
  return createSelector([localStateSelector], (ls) => ls.mode);
};

export const makeHeadingSelector = () => {
  const isLinkSentSelector = makeLocalStateIsLinkSentSelector();
  const modeSelector = makeLocalStateModeSelector();
  const i18nSelector = makeModuleI18nSelector();

  const headingSelector = getSettingValueSelector(TargetSelector.Entity, "heading");
  return createSelector(
    [i18nSelector, modeSelector, isLinkSentSelector, headingSelector],
    (i18n, mode, isLinkSent, heading) => {
      switch (mode) {
        case "expiredLink":
          return i18n.resetLinkExpired;
        case "login":
          return heading;
        case "resetPassword":
          return i18n.resetYourPassword;
        case "sendResetLink":
          return isLinkSent ? i18n.resetLinkSent : i18n.resetYourPassword;
      }
    }
  );
};

export const makeHintSelector = () => {
  const isLinkSentSelector = makeLocalStateIsLinkSentSelector();
  const modeSelector = makeLocalStateModeSelector();
  const i18nSelector = makeModuleI18nSelector();

  const hintSelector = getSettingValueSelector(TargetSelector.Entity, "hint");
  return createSelector(
    [i18nSelector, modeSelector, isLinkSentSelector, hintSelector],
    (i18n, mode, isLinkSent, hint) => {
      switch (mode) {
        case "login":
          return hint;
        case "resetPassword":
          return i18n.resetPasswordHint;
        case "sendResetLink":
          return isLinkSent ? i18n.infoSendResetLinkSent : i18n.infoSendResetLink;
      }
    }
  );
};

export const makeLinkInfoSelector = () => {
  const modeSelector = makeLocalStateModeSelector();
  const i18nSelector = makeModuleI18nSelector();

  return createSelector([i18nSelector, modeSelector], (i18n, mode): LoginForm.View.LinkInfo | null => {
    switch (mode) {
      case "login":
        return {
          caption: i18n.resetMyPassword,
          goTo: "sendResetLink"
        };
      case "sendResetLink":
        return {
          caption: i18n.returnToLogin,
          goTo: "login"
        };
      default:
        return null;
    }
  });
};
